import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export const Footer = () => {
  const { user } = useAuth();
  // Phase 2 — hide public Explore + Get Involved sections for signed-in
  // members and prospects (they live inside role-specific dashboards
  // and shouldn't see public-marketing surfaces). Other roles +
  // unauthenticated visitors keep the full footer.
  const hidePublicSections = ['member', 'prospect'].includes(user?.role);

  return (
    <footer className="bg-card border-t border-border mt-auto" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12">
        <div
          className={`grid grid-cols-1 ${
            hidePublicSections ? 'md:grid-cols-1' : 'md:grid-cols-4'
          } gap-6 md:gap-8`}
        >
          {/* Brand */}
          <div className={hidePublicSections ? '' : 'md:col-span-2'}>
            <div className="flex items-center gap-2 mb-2">
              <img
                src="/federation-sports-monogram-v2-144.webp"
                width="56"
                height="56"
                loading="lazy"
                alt=""
                aria-hidden="true"
                className="h-12 w-12 md:h-14 md:w-14 object-contain"
              />
              <span className="font-heading font-bold text-xl uppercase tracking-tight">
                Federation Sports
              </span>
            </div>
            <p
              className="font-mono text-[10px] uppercase tracking-[0.28em] text-emerald-300/80 mb-3"
              data-testid="footer-brand-line"
            >
              Made For The Athlete · Built By The Fan
            </p>
            <p className="text-muted-foreground text-sm max-w-md leading-relaxed">
              Federation Sports is an athlete-first, member-powered professional
              football league creating new opportunities for overlooked athletes
              while communities help build real teams from the beginning.
              Qualified Front Office personnel lead professional football
              operations.
            </p>
          </div>

          {/* Explore — hidden for signed-in members & prospects */}
          {!hidePublicSections && (
            <div data-testid="footer-quick-links">
              <h4 className="font-heading font-semibold uppercase tracking-wider text-sm mb-3 md:mb-4">Explore</h4>
              <ul className="space-y-1.5 md:space-y-2">
                <li>
                  <Link
                    to="/prospects"
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors inline-block py-1"
                    data-testid="footer-browse-prospects-link"
                  >
                    Browse Prospects
                  </Link>
                </li>
              </ul>
            </div>
          )}

          {/* Get Involved — hidden for signed-in members & prospects */}
          {!hidePublicSections && (
            <div data-testid="footer-join-us">
              <h4 className="font-heading font-semibold uppercase tracking-wider text-sm mb-3 md:mb-4">Get Involved</h4>
              <ul className="space-y-1.5 md:space-y-2">
                <li>
                  <Link
                    to="/become-a-prospect"
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors inline-block py-1"
                    data-testid="footer-create-prospect-profile-link"
                  >
                    Create Free Prospect Profile
                  </Link>
                </li>
                <li>
                  <Link
                    to="/become-a-member?source=footer#founding-member-waitlist"
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors inline-block py-1"
                    data-testid="footer-explore-membership-link"
                  >
                    Join Member Waitlist
                  </Link>
                </li>
                <li>
                  <Link
                    to="/login"
                    className="text-muted-foreground hover:text-foreground text-sm transition-colors inline-block py-1"
                    data-testid="footer-sign-in-link"
                  >
                    Sign In
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </div>

        <div className="border-t border-border mt-6 md:mt-8 pt-6 md:pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-xs">
            © {new Date().getFullYear()} Federation Sports. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 md:gap-x-6">
            <Link
              to="/privacy-policy"
              className="inline-block py-2 text-muted-foreground hover:text-foreground text-xs transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fs-gold-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-fs-void-0"
              data-testid="footer-privacy-policy-link"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="inline-block py-2 text-muted-foreground hover:text-foreground text-xs transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fs-gold-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-fs-void-0"
              data-testid="footer-terms-link"
            >
              Terms of Service
            </Link>
            <Link
              to="/disclaimer"
              className="inline-block py-2 text-muted-foreground hover:text-foreground text-xs transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fs-gold-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-fs-void-0"
              data-testid="footer-disclaimer-link"
            >
              Disclaimer
            </Link>
            <Link
              to="/community-guidelines"
              className="inline-block py-2 text-muted-foreground hover:text-foreground text-xs transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fs-gold-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-fs-void-0"
              data-testid="footer-community-guidelines-link"
            >
              Community Guidelines
            </Link>
            <Link
              to="/contact"
              className="inline-block py-2 text-muted-foreground hover:text-foreground text-xs transition-colors rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fs-gold-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-fs-void-0"
              data-testid="footer-contact-link"
            >
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
